import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { apiClient } from '@/services/api';
import { trackUpgradeCtaClicked, trackCheckoutCanceled } from '@/services/analytics';
import './pricing.css';

const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID || '';
const ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID || '';

const FREE_FEATURES = [
  '10 ATS quick analyses / month',
  '5 full analyses / month',
  '3 CV generations / month',
  '2 cover letters / month',
  'Job application tracker (up to 10)',
  'Score sharing badge',
];

const PRO_FEATURES = [
  '50 ATS quick analyses / month',
  'Unlimited full ATS analyses',
  '20 tailored CV generations / month',
  '10 cover letter generations / month',
  'Unlimited job tracking',
  'Priority AI (faster responses)',
  'All future features included',
];

export function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Track checkout cancellation (user returned from Stripe without paying)
  useEffect(() => {
    if (searchParams.get('canceled') === '1') {
      trackCheckoutCanceled();
    }
  }, [searchParams]);

  async function handleUpgrade() {
    if (!isAuthenticated) {
      navigate('/register?next=/pricing');
      return;
    }

    const priceId = billing === 'annual' ? ANNUAL_PRICE_ID : MONTHLY_PRICE_ID;
    if (!priceId) {
      // Stripe not yet configured — show coming soon
      alert('Payment is being set up. Check back shortly!');
      return;
    }

    trackUpgradeCtaClicked('pricing_page', { billingPeriod: billing, priceId });
    setLoading(true);
    try {
      const { data } = await apiClient.post<{ url: string }>('/api/v1/billing/checkout', { priceId });
      window.location.href = data.url;
    } catch (err: any) {
      const raw = err?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : raw?.message || err?.message || 'Unknown error';
      console.error('[PricingPage] Checkout failed:', msg, err?.response?.status, err?.response?.data);
      alert(`Checkout error: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pricing-page">
      <div className="pricing-page__inner">
        <div className="pricing-page__header">
          <h1 className="pricing-page__title">Simple, honest pricing</h1>
          <p className="pricing-page__subtitle">
            Start free. Upgrade when you need more analyses.
          </p>

          <div className="pricing-billing-toggle">
            <button
              className={`pricing-billing-toggle__btn ${billing === 'monthly' ? 'active' : ''}`}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              className={`pricing-billing-toggle__btn ${billing === 'annual' ? 'active' : ''}`}
              onClick={() => setBilling('annual')}
            >
              Annual
              <span className="pricing-billing-toggle__badge">Save 42%</span>
            </button>
          </div>
        </div>

        <div className="pricing-cards">
          {/* Free tier */}
          <div className="pricing-card pricing-card--free">
            <div className="pricing-card__header">
              <h2 className="pricing-card__name">Free</h2>
              <div className="pricing-card__price">
                <span className="pricing-card__amount">$0</span>
                <span className="pricing-card__period">forever</span>
              </div>
            </div>
            <ul className="pricing-card__features">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="pricing-card__feature">
                  <svg className="pricing-card__check" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
            <button
              className="pricing-card__cta pricing-card__cta--secondary"
              onClick={() => navigate('/register')}
            >
              Get started free
            </button>
          </div>

          {/* Pro tier */}
          <div className="pricing-card pricing-card--pro">
            <div className="pricing-card__popular-badge">Most popular</div>
            <div className="pricing-card__header">
              <h2 className="pricing-card__name">Pro</h2>
              <div className="pricing-card__price">
                <span className="pricing-card__amount">
                  {billing === 'annual' ? '$8.25' : '$17'}
                </span>
                <span className="pricing-card__period">/ month</span>
              </div>
              {billing === 'annual' && (
                <p className="pricing-card__billed-note">Billed $99/year</p>
              )}
            </div>
            <ul className="pricing-card__features">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="pricing-card__feature">
                  <svg className="pricing-card__check pricing-card__check--pro" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
            <button
              className="pricing-card__cta pricing-card__cta--primary"
              onClick={handleUpgrade}
              disabled={loading}
            >
              {loading ? 'Loading...' : `Upgrade to Pro — ${billing === 'annual' ? '$99/year' : '$17/month'}`}
            </button>
            <p className="pricing-card__guarantee">Cancel anytime · Secure payment via Stripe</p>
          </div>
        </div>

        <div className="pricing-faq">
          <h2 className="pricing-faq__title">Questions</h2>
          <div className="pricing-faq__item">
            <strong>Can I switch plans?</strong>
            <p>Yes — upgrade or cancel anytime from your account settings. No lock-in.</p>
          </div>
          <div className="pricing-faq__item">
            <strong>What happens when I hit the free limit?</strong>
            <p>Your quota resets on the 1st of each month. You can also invite friends to earn extra analyses.</p>
          </div>
          <div className="pricing-faq__item">
            <strong>Is my resume data safe?</strong>
            <p>Yes. Your data is processed in real-time and stored securely. We never share it with employers.</p>
          </div>
          <div className="pricing-faq__item">
            <strong>Do you offer refunds?</strong>
            <p>Yes — if you're not satisfied within 7 days, email us and we'll refund immediately.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
